/**
 * Reglas de disponibilidad de amenities (R6).
 *
 * Un amenity puede tener fechas bloqueadas donde NO se puede reservar:
 *  - blockedDates: ["2026-12-24", ...] fechas puntuales (ISO "YYYY-MM-DD").
 *  - recurringBlockedDates: ["12-24", "12-25", "01-01"] fechas recurrentes ("MM-DD",
 *    se bloquean todos los años).
 *
 * Además, availableDays limita los días de la semana en que se puede reservar.
 */

/** Normaliza una fecha a ISO "YYYY-MM-DD" (acepta Date o string). */
function toIso(dateOrIso) {
  if (!dateOrIso) return "";
  if (typeof dateOrIso === "string") {
    // "YYYY-MM-DD" o "YYYY-MM-DDT..." → tomar los primeros 10
    const m = dateOrIso.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(dateOrIso);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  }
  if (dateOrIso instanceof Date && !isNaN(dateOrIso.getTime())) {
    const y = dateOrIso.getFullYear();
    const mo = String(dateOrIso.getMonth() + 1).padStart(2, "0");
    const da = String(dateOrIso.getDate()).padStart(2, "0");
    return `${y}-${mo}-${da}`;
  }
  return "";
}

/**
 * ¿La fecha está bloqueada para este amenity?
 * @param {Object} config - documento del amenity (con blockedDates/recurringBlockedDates).
 * @param {string|Date} dateOrIso - fecha a evaluar.
 * @returns {boolean}
 */
export function isDateBlocked(config, dateOrIso) {
  const iso = toIso(dateOrIso);
  if (!iso) return false;
  const blocked = Array.isArray(config?.blockedDates) ? config.blockedDates : [];
  if (blocked.includes(iso)) return true;
  const recurring = Array.isArray(config?.recurringBlockedDates) ? config.recurringBlockedDates : [];
  const mmdd = iso.slice(5); // "MM-DD"
  return recurring.includes(mmdd);
}

/**
 * ¿La fecha es de SORTEO para este amenity? (alta demanda: el residente se anota y
 * el admin sortea quién la usa; sólo el ganador paga). Separado de las bloqueadas.
 *  - raffleDates: ["2026-12-31", ...] puntuales (ISO "YYYY-MM-DD").
 *  - recurringRaffleDates: ["12-31", "01-01", ...] recurrentes ("MM-DD").
 */
export function isDateRaffle(config, dateOrIso) {
  const iso = toIso(dateOrIso);
  if (!iso) return false;
  const raffle = Array.isArray(config?.raffleDates) ? config.raffleDates : [];
  if (raffle.includes(iso)) return true;
  const recurring = Array.isArray(config?.recurringRaffleDates) ? config.recurringRaffleDates : [];
  return recurring.includes(iso.slice(5));
}

/**
 * ¿El amenity acepta reservas ese día de la semana? (availableDays: 0=Dom..6=Sáb)
 * Si no hay availableDays configurados, se asume disponible todos los días.
 */
export function isDayAvailable(config, dateOrIso) {
  const iso = toIso(dateOrIso);
  if (!iso) return true;
  const days = config?.availableDays;
  if (!Array.isArray(days) || days.length === 0) return true;
  const dow = new Date(`${iso}T12:00:00`).getDay();
  return days.includes(dow);
}

/**
 * ¿Se puede reservar el amenity esa fecha? (combina día de semana + fechas bloqueadas).
 * @returns {{ ok: boolean, reason: string }}
 */
export function canReserveOnDate(config, dateOrIso) {
  if (isDateBlocked(config, dateOrIso)) {
    return { ok: false, reason: "Fecha bloqueada para reservas" };
  }
  if (isDateRaffle(config, dateOrIso)) {
    return { ok: false, reason: "Fecha de sorteo: anotate al sorteo en lugar de reservar" };
  }
  if (!isDayAvailable(config, dateOrIso)) {
    return { ok: false, reason: "El amenity no está disponible ese día de la semana" };
  }
  return { ok: true, reason: "" };
}
